/**
 * src/features/printing/lib/kitchenPrintBroadcast.ts
 *
 * Mécanisme anti-double impression KOT entre la caisse (qui imprime via picker)
 * et le KDS (qui auto-imprime sur event realtime INSERT).
 *
 * Stratégie : avant tout INSERT kitchen_orders côté caisse, on broadcast l'order_number
 * sur le canal `tenant:<id>:kitchen_print_skip`. Le KDS (s'il est ouvert) ajoute cet
 * order_number à son Set de skips. Quand l'event realtime INSERT arrive, il check
 * le Set et ne déclenche pas son auto-print.
 *
 * Le broadcast est envoyé AVANT l'INSERT (le order_number est connu en amont) pour
 * éliminer la race entre arrivée du broadcast et arrivée de l'event INSERT.
 *
 * Invariants :
 *   4.A.10 — tenant_id pris en paramètre (l'appelant l'a via JWT/useEffectiveTenantId)
 *   4.A.15 — payload broadcast = { orderNumber } seulement, pas de PII
 *   4.B.4  — 0 any
 */

// eslint-disable-next-line no-restricted-imports -- Realtime broadcast nécessite l'accès direct au client Supabase. Migration broadcast→DB row+RLS tracée dans l'issue #112 (isolation cross-tenant). Voir feedback_visionpos_critical_priorities.
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useRef } from 'react';

const CHANNEL_PREFIX = 'kitchen-print-skip';

function channelName(tenantId: string): string {
  return `tenant:${tenantId}:${CHANNEL_PREFIX}`;
}

/**
 * Émet un broadcast skip pour un order_number donné.
 * À appeler AVANT l'INSERT kitchen_orders quand on imprime déjà côté caisse via picker.
 *
 * Best-effort : si le canal n'est pas joignable, on continue silencieusement
 * (le pire qui peut arriver = double impression, pas un blocage fonctionnel).
 */
export async function broadcastKitchenPrintSkip(
  tenantId: string,
  orderNumber: string,
): Promise<void> {
  if (!tenantId || !orderNumber) return;
  try {
    const channel = supabase.channel(channelName(tenantId));
    await channel.subscribe();
    await channel.send({
      type: 'broadcast',
      event: 'skip',
      payload: { orderNumber },
    });
    await supabase.removeChannel(channel);
  } catch {
    // Best-effort : un échec broadcast ne doit pas bloquer l'envoi cuisine.
  }
}

/**
 * Hook qui maintient un Set des order_numbers à skipper côté KDS.
 * À utiliser depuis useKitchenOrders pour court-circuiter l'auto-print sur events
 * INSERT dont l'order_number a été pré-annoncé par la caisse.
 *
 * Le ref est stable entre re-renders ; le caller peut le checker dans son handler.
 */
export function useKitchenPrintSkipChannel(tenantId: string | null): {
  shouldSkip: (orderNumber: string | null | undefined) => boolean;
} {
  const skippedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!tenantId) return;
    const channel = supabase.channel(channelName(tenantId));
    channel
      .on('broadcast', { event: 'skip' }, (msg) => {
        const orderNumber = (msg.payload as { orderNumber?: unknown })?.orderNumber;
        if (typeof orderNumber === 'string' && orderNumber.length > 0) {
          skippedRef.current.add(orderNumber);
        }
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [tenantId]);

  return {
    shouldSkip: (orderNumber) => {
      if (!orderNumber) return false;
      return skippedRef.current.has(orderNumber);
    },
  };
}
